// Perimeter ring: per-edge rows offset EDGE_OFFSET inward, trimmed by
// EDGE_OFFSET at both ends so right-angle corners produce one shared corner
// can instead of two colliding ones. In narrow rooms, opposing (anti-parallel)
// rows that crowd each other are merged onto the centerline instead of one
// wall-hugging row winning arbitrarily. Design doc 2026-08-06 §4.
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const {
  signedPolygonArea,
  minDistanceToPolygonEdges,
  inwardEdgeNormal,
} = require('./polygonMath');

function perimeterCandidates(floorPolygon, C) {
  const windingSign = signedPolygonArea(floorPolygon) > 0 ? 1 : -1;
  const n = floorPolygon.length;
  const raw = [];
  for (let i = 0; i < n; i++) {
    const a = floorPolygon[i];
    const b = floorPolygon[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const rowLen = len - 2 * C.EDGE_OFFSET;
    if (rowLen < 1e-9) continue; // edge too short for a trimmed row
    const ux = (b.x - a.x) / len;
    const uz = (b.z - a.z) / len;
    const normal = inwardEdgeNormal(floorPolygon, i, windingSign);
    if (!normal) continue;
    const count = Math.max(1, Math.round(rowLen / C.PERIMETER_SPACING));
    const step = rowLen / count;
    for (let j = 0; j <= count; j++) {
      const t = C.EDGE_OFFSET + j * step;
      raw.push({
        x: a.x + ux * t + normal.x * C.EDGE_OFFSET,
        z: a.z + uz * t + normal.z * C.EDGE_OFFSET,
        normal,
      });
    }
  }

  const kept = [];
  for (const c of raw) {
    if (!isPointInsidePolygon(c, floorPolygon)) continue;
    if (
      minDistanceToPolygonEdges(c, floorPolygon) <
      C.MIN_EDGE_CLEARANCE - 1e-9
    )
      continue;
    const crowdedIndex = kept.findIndex(
      (k) => Math.hypot(k.x - c.x, k.z - c.z) < C.MIN_CAN_SEPARATION
    );
    if (crowdedIndex === -1) {
      kept.push(c);
      continue;
    }
    const k = kept[crowdedIndex];
    const antiParallel =
      c.normal.x * k.normal.x + c.normal.z * k.normal.z < -0.9;
    if (antiParallel) {
      // Narrow room: this candidate's row and the already-kept row come
      // from opposing walls closer than MIN_CAN_SEPARATION apart. Replace
      // the kept can with the midpoint (on the room's centerline) instead
      // of dropping c, so narrow rooms get a centered row instead of an
      // arbitrary wall-hugging one. Keep k's normal so obstruction-shift
      // direction stays defined (fails safe).
      kept[crowdedIndex] = {
        x: (c.x + k.x) / 2,
        z: (c.z + k.z) / 2,
        normal: k.normal,
      };
    }
    // else: crowded but not anti-parallel — e.g. two perpendicular edges'
    // rows meeting at a corner. Drop c, matching today's dedupe behavior.
  }
  return kept;
}

module.exports = { perimeterCandidates };
